import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { localeOf, t } from '@aquilixshop/i18n';
import { intlDayNames, intlMonthNames } from '../_shared/dateNames.js';
import { useEffect, useState } from 'react';
import { marginFields, visibilityField } from '../_shared/blockFields.js';
import { marginStyle, num, str, visibilityCss } from '../_shared/blockRender.js';
import { useProductState } from '../_shared/productContext.js';
import { isThemeColorToken } from '../../theme/colorTokens.js';
/**
 * Shrine `estimated-shipping` (preset "Estimated shipping", category "Shipping information") —
 * config-by-config. Renders an estimated-delivery line: an icon + a richtext message in which the
 * `[start_date]` / `[end_date]` placeholders are replaced by dates computed from a min/max
 * BUSINESS-DAYS range. The math runs client-side (re-anchored on "today" after hydration); the SSR
 * pass renders a sensible default off the server's current date so the line is correct without JS
 * and degrades gracefully.
 *
 * The full Shrine schema (58 settings) is reproduced and WIRED here, grouped exactly as Shrine groups
 * it: Icon, Text line #1, Text line #2, Styling, Shipping, Margins, Visibility.
 *
 * ID MAPPING (our camelCase id ← Shrine snake_case id — kept stable, never renamed):
 *   icon←icon, filledIcon←filled_icon, customIcon←custom_icon, iconSizePx←icon_size_px,
 *   iconAlignment←icon_alignment, message←message, line1Size←line1_size, line1Color←line1_color,
 *   line1CustomColor←line1_custom_color, line1StartHighlight←line1_start_highlight,
 *   line1StartTextColor←line1_start_text_color, line1StartBgColor←line1_start_bg_color,
 *   line1StartBgTextColor←line1_start_bg_text_color, line1EndHighlight←line1_end_highlight,
 *   line1EndTextColor←line1_end_text_color, line1EndBgColor←line1_end_bg_color,
 *   line1EndBgTextColor←line1_end_bg_text_color, line2Text←line2_text, line2Size←line2_size, … (the
 *   same 12-knob shape repeated for line 2), bgType←bg_type, bgColor←bg_color, bgGradient←bg_gradient
 *   (our `gradient` field type = Shrine `color_background`), bgOpacity←bg_opacity,
 *   borderThickness←border_thickness, borderColor←border_color, borderStyle←border_style,
 *   cornerRadius←corner_radius, paddingY←padding_y, paddingX←padding_x, enableShadow←enable_shadow,
 *   shadow*←shadow_*, width←width, alignment←alignment, minShippingDays←min_shipping_days,
 *   maxShippingDays←max_shipping_days, line2MinShippingDays←line2_min_shipping_days,
 *   line2MaxShippingDays←line2_max_shipping_days, dateFormat←date_format, daysLabels←days_labels,
 *   monthsLabels←months_labels, daysToSkip←days_to_skip, datesToSkip←dates_to_skip.
 *
 * NOTE on conditional visibility: our `visibleWhen` predicate is equals/notEquals only (no numeric `>`
 * and no "message contains [token]" string-search). So:
 *  - the highlight-detail fields (text/bg colors gated on `*_highlight == color|background`) carry a
 *    faithful `visibleWhen`; the *additional* Shrine gate "message contains [start_date]" can't be
 *    expressed and is left always-applicable in the inspector — the RENDER still no-ops when the token
 *    is absent (nothing to wrap).
 *  - `line1_custom_color` / `line2_*` (gated on `line1Color == custom` / `line2_text != blank`),
 *    `bgColor`/`bgGradient`/`bgOpacity` (gated on `bgType`), and the shadow_* fields (gated on the
 *    `enableShadow` boolean) carry a faithful `visibleWhen`.
 *  - the Styling fields Shrine gates on the COMPOUND `bg_type != none OR border_thickness > 0 OR
 *    enable_shadow` (corner_radius, padding_x/y, width, alignment) are left always-visible in the
 *    inspector — the RENDER honours them only when a box actually exists (`hasBox`).
 *
 * DEFERRED (Shopify-platform settings, justified):
 *  - `icon` is rendered from a small curated INLINE-SVG set (truck / box / clock / shield), NOT the
 *    Material-Symbols glyph FONT Shrine resolves via `{% render 'material-icon' %}` — that web-font is a
 *    Shopify-platform dependency. `filledIcon` toggles between the filled and outlined SVG variants
 *    (mirrors the Material FILL axis intent).
 *  - `customIcon` (image_picker) is rendered when set (an <img>), but its VALUE is a Shopify-CDN asset
 *    URL in Shrine; our asset/upload pipeline supplies the URL — the control + render are present.
 */
const DEFAULT_MESSAGE = {
    format: 'html',
    html: '<p>Recevez-le entre <strong>[start_date]</strong> et <strong>[end_date]</strong>.</p>',
};
const DEFAULT_DAYS = 'Lundi, Mardi, Mercredi, Jeudi, Vendredi, Samedi, Dimanche';
const DEFAULT_MONTHS = 'Janvier, Février, Mars, Avril, Mai, Juin, Juillet, Août, Septembre, Octobre, Novembre, Décembre';
const HIGHLIGHT_OPTIONS = [
    { value: 'none', label: 'None' },
    { value: 'color', label: 'Text color' },
    { value: 'background', label: 'Background' },
];
const LINE_COLOR_OPTIONS = [
    { value: 'default', label: 'Default' },
    { value: 'accent-1', label: 'Accent 1' },
    { value: 'accent-2', label: 'Accent 2' },
    { value: 'custom', label: 'Custom' },
];
export const estimatedShippingBlockSchema = {
    type: 'estimated-shipping',
    name: 'Estimated shipping',
    max: 1,
    fields: [
        visibilityField,
        // ── Icon ───────────────────────────────────────────────────────────────────
        {
            id: 'icon',
            type: 'icon',
            label: 'Icon',
            group: 'Icon',
            info: 'Choose the delivery icon (curated set: truck, box, clock, shield).',
            default: 'truck',
        },
        { id: 'filledIcon', type: 'checkbox', label: 'Filled icon', group: 'Icon', default: false },
        { id: 'customIcon', type: 'image', label: 'Custom icon', group: 'Icon' },
        {
            id: 'iconSizePx',
            type: 'range',
            label: 'Icon size',
            group: 'Icon',
            min: 16,
            max: 64,
            step: 2,
            default: 28,
            unit: 'px',
        },
        {
            id: 'iconAlignment',
            type: 'select',
            label: 'Icon alignment',
            group: 'Icon',
            default: 'top',
            options: [
                { value: 'top', label: 'Top' },
                { value: 'middle', label: 'Middle' },
            ],
        },
        // ── Text line #1 ────────────────────────────────────────────────────────────
        {
            id: 'message',
            type: 'richtext',
            label: 'Message',
            group: 'Text line #1',
            info: 'Use [start_date] and [end_date] — they are replaced by the computed dates.',
            default: DEFAULT_MESSAGE,
        },
        {
            id: 'line1Size',
            type: 'range',
            label: 'Font size',
            group: 'Text line #1',
            min: 10,
            max: 24,
            step: 1,
            default: 14,
            unit: 'px',
        },
        {
            id: 'line1Color',
            type: 'select',
            label: 'Text color',
            group: 'Text line #1',
            default: 'default',
            options: LINE_COLOR_OPTIONS,
        },
        {
            id: 'line1CustomColor',
            type: 'color',
            label: 'Custom text color',
            group: 'Text line #1',
            default: { ref: 'custom', value: '#dd1d1d' },
            visibleWhen: { field: 'line1Color', equals: 'custom' },
        },
        {
            id: 'line1StartHighlight',
            type: 'select',
            label: '[start_date] highlight',
            group: 'Text line #1',
            default: 'none',
            options: HIGHLIGHT_OPTIONS,
        },
        {
            id: 'line1StartTextColor',
            type: 'color',
            label: '[start_date] text color',
            group: 'Text line #1',
            default: { ref: 'theme', token: 'accent' },
            visibleWhen: { field: 'line1StartHighlight', equals: 'color' },
        },
        {
            id: 'line1StartBgColor',
            type: 'color',
            label: '[start_date] background',
            group: 'Text line #1',
            default: { ref: 'custom', value: '#0CB137' },
            visibleWhen: { field: 'line1StartHighlight', equals: 'background' },
        },
        {
            id: 'line1StartBgTextColor',
            type: 'color',
            label: '[start_date] background text color',
            group: 'Text line #1',
            default: { ref: 'custom', value: '#ffffff' },
            visibleWhen: { field: 'line1StartHighlight', equals: 'background' },
        },
        {
            id: 'line1EndHighlight',
            type: 'select',
            label: '[end_date] highlight',
            group: 'Text line #1',
            default: 'none',
            options: HIGHLIGHT_OPTIONS,
        },
        {
            id: 'line1EndTextColor',
            type: 'color',
            label: '[end_date] text color',
            group: 'Text line #1',
            default: { ref: 'theme', token: 'accent' },
            visibleWhen: { field: 'line1EndHighlight', equals: 'color' },
        },
        {
            id: 'line1EndBgColor',
            type: 'color',
            label: '[end_date] background',
            group: 'Text line #1',
            default: { ref: 'custom', value: '#0CB137' },
            visibleWhen: { field: 'line1EndHighlight', equals: 'background' },
        },
        {
            id: 'line1EndBgTextColor',
            type: 'color',
            label: '[end_date] background text color',
            group: 'Text line #1',
            default: { ref: 'custom', value: '#ffffff' },
            visibleWhen: { field: 'line1EndHighlight', equals: 'background' },
        },
        // ── Text line #2 ────────────────────────────────────────────────────────────
        {
            id: 'line2Text',
            type: 'richtext',
            label: 'Message',
            group: 'Text line #2',
            info: 'Optional second line. Leave empty to hide. Supports [start_date] / [end_date].',
        },
        {
            id: 'line2Size',
            type: 'range',
            label: 'Font size',
            group: 'Text line #2',
            min: 8,
            max: 20,
            step: 1,
            default: 12,
            unit: 'px',
            visibleWhen: { field: 'line2Text', notEquals: '' },
        },
        {
            id: 'line2Color',
            type: 'select',
            label: 'Text color',
            group: 'Text line #2',
            default: 'default',
            options: LINE_COLOR_OPTIONS,
            visibleWhen: { field: 'line2Text', notEquals: '' },
        },
        {
            id: 'line2CustomColor',
            type: 'color',
            label: 'Custom text color',
            group: 'Text line #2',
            default: { ref: 'custom', value: '#dd1d1d' },
            visibleWhen: { field: 'line2Color', equals: 'custom' },
        },
        {
            id: 'line2StartHighlight',
            type: 'select',
            label: '[start_date] highlight',
            group: 'Text line #2',
            default: 'none',
            options: HIGHLIGHT_OPTIONS,
        },
        {
            id: 'line2StartTextColor',
            type: 'color',
            label: '[start_date] text color',
            group: 'Text line #2',
            default: { ref: 'theme', token: 'accent' },
            visibleWhen: { field: 'line2StartHighlight', equals: 'color' },
        },
        {
            id: 'line2StartBgColor',
            type: 'color',
            label: '[start_date] background',
            group: 'Text line #2',
            default: { ref: 'custom', value: '#0CB137' },
            visibleWhen: { field: 'line2StartHighlight', equals: 'background' },
        },
        {
            id: 'line2StartBgTextColor',
            type: 'color',
            label: '[start_date] background text color',
            group: 'Text line #2',
            default: { ref: 'custom', value: '#ffffff' },
            visibleWhen: { field: 'line2StartHighlight', equals: 'background' },
        },
        {
            id: 'line2EndHighlight',
            type: 'select',
            label: '[end_date] highlight',
            group: 'Text line #2',
            default: 'none',
            options: HIGHLIGHT_OPTIONS,
        },
        {
            id: 'line2EndTextColor',
            type: 'color',
            label: '[end_date] text color',
            group: 'Text line #2',
            default: { ref: 'theme', token: 'accent' },
            visibleWhen: { field: 'line2EndHighlight', equals: 'color' },
        },
        {
            id: 'line2EndBgColor',
            type: 'color',
            label: '[end_date] background',
            group: 'Text line #2',
            default: { ref: 'custom', value: '#0CB137' },
            visibleWhen: { field: 'line2EndHighlight', equals: 'background' },
        },
        {
            id: 'line2EndBgTextColor',
            type: 'color',
            label: '[end_date] background text color',
            group: 'Text line #2',
            default: { ref: 'custom', value: '#ffffff' },
            visibleWhen: { field: 'line2EndHighlight', equals: 'background' },
        },
        // ── Styling ─────────────────────────────────────────────────────────────────
        {
            id: 'bgType',
            type: 'select',
            label: 'Background',
            group: 'Styling',
            default: 'none',
            options: [
                { value: 'none', label: 'None' },
                { value: 'solid', label: 'Solid color' },
                { value: 'gradient', label: 'Gradient color' },
            ],
        },
        {
            id: 'bgColor',
            type: 'color',
            label: 'Background color',
            group: 'Styling',
            default: { ref: 'theme', token: 'accent-2' },
            visibleWhen: { field: 'bgType', equals: 'solid' },
        },
        {
            id: 'bgGradient',
            type: 'gradient',
            label: 'Background gradient',
            group: 'Styling',
            default: 'linear-gradient(135deg, rgba(232,220,255,1) 0%, rgba(255,220,232,1) 100%)',
            visibleWhen: { field: 'bgType', equals: 'gradient' },
        },
        {
            id: 'bgOpacity',
            type: 'range',
            label: 'Background opacity',
            group: 'Styling',
            min: 0,
            max: 100,
            step: 5,
            default: 100,
            unit: '%',
            visibleWhen: { field: 'bgType', equals: 'solid' },
        },
        {
            id: 'borderThickness',
            type: 'range',
            label: 'Border thickness',
            group: 'Styling',
            min: 0,
            max: 8,
            step: 1,
            default: 0,
            unit: 'px',
        },
        {
            id: 'borderColor',
            type: 'color',
            label: 'Border color',
            group: 'Styling',
            default: { ref: 'custom', value: '#000000' },
            visibleWhen: { field: 'borderThickness', notEquals: 0 },
        },
        {
            id: 'borderStyle',
            type: 'select',
            label: 'Border style',
            group: 'Styling',
            default: 'solid',
            options: [
                { value: 'solid', label: 'Solid' },
                { value: 'dashed', label: 'Dashed' },
            ],
            visibleWhen: { field: 'borderThickness', notEquals: 0 },
        },
        {
            id: 'cornerRadius',
            type: 'range',
            label: 'Corner radius',
            group: 'Styling',
            min: 0,
            max: 50,
            step: 2,
            default: 10,
            unit: 'px',
        },
        {
            id: 'paddingY',
            type: 'range',
            label: 'Vertical padding',
            group: 'Styling',
            min: 0,
            max: 40,
            step: 2,
            default: 10,
            unit: 'px',
        },
        {
            id: 'paddingX',
            type: 'range',
            label: 'Horizontal padding',
            group: 'Styling',
            min: 0,
            max: 40,
            step: 2,
            default: 12,
            unit: 'px',
        },
        {
            id: 'enableShadow',
            type: 'checkbox',
            label: 'Enable shadow',
            group: 'Styling',
            default: false,
        },
        {
            id: 'shadowColor',
            type: 'color',
            label: 'Shadow color',
            group: 'Styling',
            default: { ref: 'custom', value: '#000000' },
            visibleWhen: { field: 'enableShadow', equals: true },
        },
        {
            id: 'shadowOpacity',
            type: 'range',
            label: 'Shadow opacity',
            group: 'Styling',
            min: 0,
            max: 100,
            step: 5,
            default: 15,
            unit: '%',
            visibleWhen: { field: 'enableShadow', equals: true },
        },
        {
            id: 'shadowHorizontalOffset',
            type: 'range',
            label: 'Shadow horizontal offset',
            group: 'Styling',
            min: -50,
            max: 50,
            step: 2,
            default: 0,
            visibleWhen: { field: 'enableShadow', equals: true },
        },
        {
            id: 'shadowVerticalOffset',
            type: 'range',
            label: 'Shadow vertical offset',
            group: 'Styling',
            min: -50,
            max: 50,
            step: 2,
            default: 10,
            visibleWhen: { field: 'enableShadow', equals: true },
        },
        {
            id: 'shadowBlur',
            type: 'range',
            label: 'Shadow blur',
            group: 'Styling',
            min: 0,
            max: 100,
            step: 2,
            default: 20,
            visibleWhen: { field: 'enableShadow', equals: true },
        },
        {
            id: 'shadowSpread',
            type: 'range',
            label: 'Shadow spread',
            group: 'Styling',
            min: -50,
            max: 50,
            step: 2,
            default: 0,
            visibleWhen: { field: 'enableShadow', equals: true },
        },
        {
            id: 'width',
            aiConfigurable: true,
            type: 'select',
            label: 'Width',
            group: 'Styling',
            default: 'full',
            options: [
                { value: 'fit_content', label: 'Fit content' },
                { value: 'full', label: 'Full' },
            ],
        },
        {
            id: 'alignment',
            aiConfigurable: true,
            type: 'select',
            label: 'Alignment',
            group: 'Styling',
            default: 'flex-start',
            options: [
                { value: 'flex-start', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'flex-end', label: 'Right' },
            ],
        },
        // ── Shipping ────────────────────────────────────────────────────────────────
        {
            id: 'minShippingDays',
            type: 'number',
            label: 'Minimum shipping days (line 1)',
            group: 'Shipping',
            info: 'Business days until the earliest delivery date.',
            default: 7,
            min: 0,
            step: 1,
        },
        {
            id: 'maxShippingDays',
            type: 'number',
            label: 'Maximum shipping days (line 1)',
            group: 'Shipping',
            info: 'Business days until the latest delivery date.',
            default: 15,
            min: 0,
            step: 1,
        },
        {
            id: 'line2MinShippingDays',
            type: 'number',
            label: 'Minimum shipping days (line 2)',
            group: 'Shipping',
            default: 7,
            min: 0,
            step: 1,
            visibleWhen: { field: 'line2Text', notEquals: '' },
        },
        {
            id: 'line2MaxShippingDays',
            type: 'number',
            label: 'Maximum shipping days (line 2)',
            group: 'Shipping',
            default: 15,
            min: 0,
            step: 1,
            visibleWhen: { field: 'line2Text', notEquals: '' },
        },
        {
            id: 'dateFormat',
            type: 'select',
            label: 'Date format',
            group: 'Shipping',
            default: 'day_mm_dd',
            options: [
                { value: 'day_mm_dd', label: 'Mon, January 5' },
                { value: 'day_dd_mm', label: 'Mon, 5 January' },
                { value: 'mm_dd', label: 'January 5' },
                { value: 'dd_mm', label: '5 January' },
                { value: 'dd_mm_no_dot', label: '5 Jan' },
                { value: 'day_dd_mm_numeric', label: 'Mon, 05/01' },
                { value: 'dd_mm_numeric', label: '05/01' },
            ],
        },
        {
            id: 'daysLabels',
            type: 'text',
            label: 'Day names',
            group: 'Shipping',
            info: 'Comma-separated, Monday → Sunday. Localize here.',
            default: DEFAULT_DAYS,
            defaultKey: 'section.day_names',
        },
        {
            id: 'monthsLabels',
            type: 'text',
            label: 'Month names',
            group: 'Shipping',
            info: 'Comma-separated, January → December. Localize here.',
            default: DEFAULT_MONTHS,
            defaultKey: 'section.month_names',
        },
        {
            id: 'daysToSkip',
            type: 'text',
            label: 'Week days to skip',
            group: 'Shipping',
            info: 'Comma-separated day names treated as non-business (e.g. Saturday, Sunday).',
            default: '',
        },
        {
            id: 'datesToSkip',
            type: 'text',
            label: 'Specific dates to skip',
            group: 'Shipping',
            info: 'Comma-separated MM-DD dates skipped every year (holidays), e.g. 12-25, 01-01.',
            default: '',
        },
        // ── Margins ─────────────────────────────────────────────────────────────────
        ...marginFields(15, 15),
    ],
};
// ── Inline-SVG icon set (outlined + filled variants — NOT the Material-Symbols web font). ──
const ICONS = {
    truck: {
        outline: 'M1 3h13v10H1V3zm13 4h4l3 3v3h-7V7zM5.5 16a2 2 0 100 4 2 2 0 000-4zm12 0a2 2 0 100 4 2 2 0 000-4z',
        filled: 'M1 3h13v10H1V3zm13 4h4l3 3v3h-7V7zM5.5 16a2 2 0 100 4 2 2 0 000-4zm12 0a2 2 0 100 4 2 2 0 000-4z',
    },
    box: {
        outline: 'M12 2l9 5v10l-9 5-9-5V7l9-5zm0 2.2L5 8l7 3.9L19 8l-7-3.8zM5 9.7V16l6 3.3v-6.3L5 9.7z',
        filled: 'M12 2l9 5v10l-9 5-9-5V7l9-5zm0 2.2L5 8l7 3.9L19 8l-7-3.8zM5 9.7V16l6 3.3v-6.3L5 9.7z',
    },
    clock: {
        outline: 'M12 2a10 10 0 100 20 10 10 0 000-20zm0 2a8 8 0 110 16 8 8 0 010-16zm-1 3v6l5 3 1-1.7-4-2.3V7h-2z',
        filled: 'M12 2a10 10 0 100 20 10 10 0 000-20zm1 5v5.4l4 2.4-1 1.7-5-3V7h2z',
    },
    shield: {
        outline: 'M12 2l8 3v6c0 5-3.4 8.5-8 11-4.6-2.5-8-6-8-11V5l8-3zm0 2.2L6 6.4V11c0 3.8 2.4 6.5 6 8.6 3.6-2.1 6-4.8 6-8.6V6.4l-6-2.2z',
        filled: 'M12 2l8 3v6c0 5-3.4 8.5-8 11-4.6-2.5-8-6-8-11V5l8-3z',
    },
};
const ICON_ALIASES = {
    local_shipping: 'truck',
    delivery: 'truck',
    shipping: 'truck',
    package: 'box',
    inventory: 'box',
    schedule: 'clock',
    time: 'clock',
    verified: 'shield',
    security: 'shield',
};
function DeliveryIcon({ id, filled, size }) {
    const key = ICONS[id] ? id : (ICON_ALIASES[id] ?? 'truck');
    const def = ICONS[key] ?? ICONS.truck ?? { filled: '', outline: '' };
    return (_jsx("svg", { viewBox: "0 0 24 24", width: size, height: size, "aria-hidden": "true", style: { flexShrink: 0, display: 'block', fill: 'currentColor' }, children: _jsx("path", { d: filled ? def.filled : def.outline }) }));
}
/** Split a comma-separated label list, trimmed, falling back to a default split. */
function splitList(raw, fallback) {
    const src = raw.trim() ? raw : fallback;
    return src
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
}
/**
 * Add `businessDays` business days to `base`, skipping configured weekday names and MM-DD dates. The
 * weekday-skip set is matched against `daysLabels` (the authored, possibly-localized names), so
 * "Saturday, Sunday" in `daysToSkip` maps back to JS getDay() via the daysLabels order
 * (Monday=index0 → JS day 1 … Sunday=index6 → JS day 0).
 */
function addBusinessDays(base, businessDays, skipWeekdayJs, skipDatesMmDd) {
    const d = new Date(base.getTime());
    let added = 0;
    // Walk day-by-day; a "skip" day does not count toward the business-day total.
    // Guard against an all-days-skipped config with a hard cap.
    let guard = 0;
    while (added < businessDays && guard < 3650) {
        d.setDate(d.getDate() + 1);
        guard += 1;
        const mmdd = `${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
        if (skipWeekdayJs.has(d.getDay()) || skipDatesMmDd.has(mmdd))
            continue;
        added += 1;
    }
    return d;
}
function pad2(n) {
    return n < 10 ? `0${n}` : String(n);
}
/** Map authored day-names (Mon→Sun order) selected in `daysToSkip` to JS getDay() indices (Sun=0). */
function weekdaySkipSet(daysLabels, daysToSkip) {
    const skip = new Set();
    const lc = daysLabels.map((s) => s.toLowerCase());
    for (const name of daysToSkip) {
        const idx = lc.indexOf(name.trim().toLowerCase());
        if (idx >= 0) {
            // daysLabels index 0 = Monday → JS day 1; index 6 = Sunday → JS day 0.
            skip.add((idx + 1) % 7);
        }
    }
    return skip;
}
/** Format a date per the Shrine `date_format` option using the authored day/month label arrays. */
function formatDate(d, fmt, days, months) {
    // days[] is Monday-first; JS getDay() is Sunday=0, so map to the Monday-first index.
    const dayName = days[(d.getDay() + 6) % 7] ?? '';
    const monthName = months[d.getMonth()] ?? '';
    const dd = d.getDate();
    const mm = d.getMonth() + 1;
    const monthShort = monthName.slice(0, 3);
    switch (fmt) {
        case 'day_dd_mm':
            return `${dayName}, ${dd} ${monthName}`;
        case 'mm_dd':
            return `${monthName} ${dd}`;
        case 'dd_mm':
            return `${dd} ${monthName}`;
        case 'dd_mm_no_dot':
            return `${dd} ${monthShort}`;
        case 'day_dd_mm_numeric':
            return `${dayName}, ${pad2(dd)}/${pad2(mm)}`;
        case 'dd_mm_numeric':
            return `${pad2(dd)}/${pad2(mm)}`;
        case 'day_mm_dd':
        default:
            return `${dayName}, ${monthName} ${dd}`;
    }
}
function resolveHex(v, fallback) {
    if (v && typeof v === 'object') {
        const c = v;
        if (typeof c.value === 'string')
            return c.value;
        // 0019 §4a: the THEME branch. It was missing entirely — this resolver only ever looked at `.value`, so a
        // { ref:'theme', token } (which has no `value` key) fell through to the frozen fallback and a re-theme
        // changed nothing here (allowlisted, finding #18).
        if (c.ref === 'theme' && typeof c.token === 'string' && isThemeColorToken(c.token))
            return `var(--aqx-theme-${c.token})`;
    }
    if (typeof v === 'string')
        return v;
    return fallback;
}
/**
 * `#rrggbb` + 0–100 opacity → `rgba()` (SSR-safe). A THEME TOKEN arrives as `var(--aqx-theme-*)`, which no hex
 * arithmetic can read, so it takes the `color-mix` path instead — the seller's theme colour AND the opacity
 * slider both survive (same idiom as blocks/text-with-icon's `alphaColor`).
 */
function hexToRgba(hex, opacityPct) {
    const a = Math.max(0, Math.min(100, opacityPct)) / 100;
    if (hex.startsWith('var(')) {
        return a >= 1 ? hex : `color-mix(in srgb, ${hex} ${Math.round(a * 100)}%, transparent)`;
    }
    const m = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim());
    if (!m || !m[1])
        return hex;
    const int = parseInt(m[1], 16);
    return `rgba(${(int >> 16) & 255}, ${(int >> 8) & 255}, ${int & 255}, ${a})`;
}
/** Pull the HTML string out of a richtext value (or accept a plain string). Empty = no line. */
function richHtml(v) {
    if (v && typeof v === 'object' && 'html' in v) {
        const html = v.html;
        return typeof html === 'string' ? html : '';
    }
    return typeof v === 'string' ? v : '';
}
/** Resolve a line's base text color (default / accent-1 / accent-2 / custom). */
function lineColor(mode, custom) {
    switch (mode) {
        case 'accent-1':
        case 'accent-2':
            return 'var(--aqx-scheme-accent)';
        case 'custom':
            return resolveHex(custom, '#dd1d1d');
        case 'default':
        default:
            return undefined;
    }
}
/** Build the inline-style attribute (as an HTML string) for a highlighted date span. */
function highlightStyle(mode, textColor, bgColor, bgTextColor) {
    if (mode === 'color') {
        return ` style="color:${resolveHex(textColor, 'var(--aqx-theme-accent)')}"`;
    }
    if (mode === 'background') {
        return ` style="background-color:${resolveHex(bgColor, '#0CB137')};color:${resolveHex(bgTextColor, '#ffffff')};padding:0 .35em;border-radius:.25em"`;
    }
    return '';
}
/** Escape HTML special chars in a computed date so it can't break out of the wrapping span. */
function esc(s) {
    return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}
/** Replace [start_date]/[end_date] in an HTML message with the (escaped, optionally-highlighted) dates. */
function fillMessage(html, start, end, hl) {
    const startSpan = `<span${highlightStyle(hl.startMode, hl.startTextColor, hl.startBgColor, hl.startBgTextColor)}>${esc(start)}</span>`;
    const endSpan = `<span${highlightStyle(hl.endMode, hl.endTextColor, hl.endBgColor, hl.endBgTextColor)}>${esc(end)}</span>`;
    return html.replace(/\[start_date\]/g, startSpan).replace(/\[end_date\]/g, endSpan);
}
/** An estimated-delivery buy-box line: delivery icon + computed "between X and Y" message(s). */
const EstimatedShippingBlock = ({ block, context }) => {
    const ps = useProductState();
    // Re-anchor the computation on the CLIENT's current date after hydration; SSR uses the server date.
    const [now, setNow] = useState(() => new Date());
    useEffect(() => {
        setNow(new Date());
    }, []);
    if (!ps)
        return null;
    const bs = block.settings;
    const cls = `aqx-esh-${block.id}`;
    const vis = visibilityCss(cls, bs.visibility);
    // ── Shipping config (shared across both lines). ──
    // The names of days and months are a DATA LIST, not a sentence — but they are still read aloud on the
    // page, so they follow the shop's language like everything else. The stored setting wins; the fallback
    // is the dictionary's, not a French constant.
    // `section.*` falls through to French in every other language by contract, so the platform's locale data
    // answers first; the dictionary is only the last resort (see `_shared/dateNames.ts`).
    const shopLocale = localeOf(context);
    const fbDays = intlDayNames(shopLocale, 'long')?.join(', ') ?? t(context, 'section.day_names');
    const fbMonths = intlMonthNames(shopLocale, 'long')?.join(', ') ?? t(context, 'section.month_names');
    const days = splitList(str(bs.daysLabels, fbDays), fbDays);
    const months = splitList(str(bs.monthsLabels, fbMonths), fbMonths);
    const skipWeekdays = weekdaySkipSet(days, splitList(str(bs.daysToSkip), ''));
    const skipDates = new Set(splitList(str(bs.datesToSkip), '').map((s) => s.replace(/\//g, '-')));
    const fmt = str(bs.dateFormat, 'day_mm_dd');
    const computeRange = (minDays, maxDays) => {
        const lo = Math.max(0, Math.min(minDays, maxDays));
        const hi = Math.max(minDays, maxDays);
        const startDate = addBusinessDays(now, lo, skipWeekdays, skipDates);
        const endDate = addBusinessDays(now, hi, skipWeekdays, skipDates);
        return {
            start: formatDate(startDate, fmt, days, months),
            end: formatDate(endDate, fmt, days, months),
        };
    };
    // ── Line 1 (always present). ──
    const line1Range = computeRange(num(bs.minShippingDays, 7), num(bs.maxShippingDays, 15));
    const line1Html = fillMessage(richHtml(bs.message) || DEFAULT_MESSAGE.html, line1Range.start, line1Range.end, {
        startMode: str(bs.line1StartHighlight, 'none'),
        startTextColor: bs.line1StartTextColor,
        startBgColor: bs.line1StartBgColor,
        startBgTextColor: bs.line1StartBgTextColor,
        endMode: str(bs.line1EndHighlight, 'none'),
        endTextColor: bs.line1EndTextColor,
        endBgColor: bs.line1EndBgColor,
        endBgTextColor: bs.line1EndBgTextColor,
    });
    const line1Size = num(bs.line1Size, 14);
    const line1Col = lineColor(str(bs.line1Color, 'default'), bs.line1CustomColor);
    // ── Line 2 (optional — hidden when empty). ──
    const line2Raw = richHtml(bs.line2Text);
    const hasLine2 = line2Raw.replace(/<[^>]*>/g, '').trim().length > 0;
    let line2Html = '';
    if (hasLine2) {
        const r2 = computeRange(num(bs.line2MinShippingDays, 7), num(bs.line2MaxShippingDays, 15));
        line2Html = fillMessage(line2Raw, r2.start, r2.end, {
            startMode: str(bs.line2StartHighlight, 'none'),
            startTextColor: bs.line2StartTextColor,
            startBgColor: bs.line2StartBgColor,
            startBgTextColor: bs.line2StartBgTextColor,
            endMode: str(bs.line2EndHighlight, 'none'),
            endTextColor: bs.line2EndTextColor,
            endBgColor: bs.line2EndBgColor,
            endBgTextColor: bs.line2EndBgTextColor,
        });
    }
    const line2Size = num(bs.line2Size, 12);
    const line2Col = lineColor(str(bs.line2Color, 'default'), bs.line2CustomColor);
    // ── Icon. ──
    const iconId = str(bs.icon, 'truck');
    const iconFilled = bs.filledIcon === true;
    const iconSize = num(bs.iconSizePx, 28);
    const customIconUrl = bs.customIcon && typeof bs.customIcon === 'object'
        ? str(bs.customIcon.url)
        : '';
    const iconAlign = str(bs.iconAlignment, 'top') === 'middle' ? 'center' : 'flex-start';
    // ── Styling group → the inner "card". ──
    const bgType = str(bs.bgType, 'none');
    const borderThickness = num(bs.borderThickness, 0);
    const enableShadow = bs.enableShadow === true;
    const hasBox = bgType !== 'none' || borderThickness > 0 || enableShadow;
    const fullWidth = str(bs.width, 'full') === 'full';
    const cardStyle = {
        display: 'flex',
        alignItems: iconAlign,
        gap: '0.6em',
        width: fullWidth ? '100%' : 'fit-content',
        color: 'var(--aqx-scheme-text)',
    };
    if (bgType === 'solid') {
        cardStyle.background = hexToRgba(resolveHex(bs.bgColor, '#BFE3FF'), num(bs.bgOpacity, 100));
    }
    else if (bgType === 'gradient') {
        cardStyle.background = str(bs.bgGradient, 'linear-gradient(135deg, rgba(232,220,255,1) 0%, rgba(255,220,232,1) 100%)');
    }
    if (borderThickness > 0) {
        cardStyle.border = `${borderThickness}px ${str(bs.borderStyle, 'solid')} ${resolveHex(bs.borderColor, '#000000')}`;
    }
    if (hasBox) {
        cardStyle.borderRadius = `${num(bs.cornerRadius, 10)}px`;
        cardStyle.padding = `${num(bs.paddingY, 10)}px ${num(bs.paddingX, 12)}px`;
    }
    if (enableShadow) {
        const sx = num(bs.shadowHorizontalOffset, 0);
        const sy = num(bs.shadowVerticalOffset, 10);
        const sb = num(bs.shadowBlur, 20);
        const ss = num(bs.shadowSpread, 0);
        const sc = hexToRgba(resolveHex(bs.shadowColor, '#000000'), num(bs.shadowOpacity, 15));
        cardStyle.boxShadow = `${sx}px ${sy}px ${sb}px ${ss}px ${sc}`;
    }
    // Outer wrapper handles alignment (only meaningful when the card is fit-content / boxed).
    const justify = hasBox || !fullWidth ? str(bs.alignment, 'flex-start') : 'stretch';
    return (_jsxs(_Fragment, { children: [vis ? _jsx("style", { dangerouslySetInnerHTML: { __html: vis } }) : null, _jsx("div", { className: cls, style: {
                    display: 'flex',
                    justifyContent: justify,
                    ...marginStyle(bs),
                }, children: _jsxs("div", { style: cardStyle, children: [_jsx("span", { style: { display: 'inline-flex', color: 'var(--aqx-scheme-accent)', lineHeight: 0 }, children: customIconUrl ? (_jsx("img", { src: customIconUrl, alt: "", width: iconSize, height: iconSize, style: { display: 'block', objectFit: 'contain' } })) : (_jsx(DeliveryIcon, { id: iconId, filled: iconFilled, size: iconSize })) }), _jsxs("span", { style: { minWidth: 0 }, children: [_jsx("span", { style: {
                                        fontSize: `${line1Size}px`,
                                        lineHeight: 1.4,
                                        color: line1Col,
                                        display: 'block',
                                    }, dangerouslySetInnerHTML: { __html: line1Html } }), hasLine2 ? (_jsx("span", { style: {
                                        fontSize: `${line2Size}px`,
                                        lineHeight: 1.4,
                                        color: line2Col ?? 'var(--aqx-scheme-muted)',
                                        display: 'block',
                                        marginTop: '0.15em',
                                    }, dangerouslySetInnerHTML: { __html: line2Html } })) : null] })] }) })] }));
};
export const estimatedShippingBlock = {
    type: 'estimated-shipping',
    schema: estimatedShippingBlockSchema,
    Component: EstimatedShippingBlock,
    meta: { label: 'Estimated shipping' },
};
//# sourceMappingURL=index.js.map